import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export default function HelpChatIcon({
  title = 'Contextual help',
  placement = 'right',
  size = 16,
  color = '#0d6efd',
  className = '',
  style = {},
  questionYOffset = 10.5,
  onOpen,
  seedQuestion,
  scope = 'site',
}) {
  const dim = Number(size) || 16;
  const mergedStyle = {
    display: 'inline-block',
    verticalAlign: 'text-bottom',
    color,
    ...style,
  };
  function defaultOpen() {
    try {
      // eslint-disable-next-line no-console
      console.debug && console.debug('[HelpChatIcon] defaultOpen: looking for launcher');
      // Try to simulate a click on the Skinny launcher button
      const host = (scope
        ? document.querySelector('[data-skinny-button][data-skinny-scope="'+scope+'"]')
        : null) || document.querySelector('[data-skinny-button]');
      const btn = host && host.shadowRoot && host.shadowRoot.querySelector('.btn');
      if (btn) {
        // eslint-disable-next-line no-console
        console.debug && console.debug('[HelpChatIcon] defaultOpen: launcher found, clicking');
        btn.click(); return true;
      }
      // eslint-disable-next-line no-console
      console.debug && console.debug('[HelpChatIcon] defaultOpen: launcher not found');
    } catch (_) {}
    return false;
  }
  function readSeedFromSession() {
    try {
      const raw = sessionStorage.getItem('SKINNY_SEED');
      // eslint-disable-next-line no-console
      console.debug && console.debug('[HelpChatIcon] session read SKINNY_SEED raw =', raw);
      if (!raw) return null;
      const j = JSON.parse(raw);
      // eslint-disable-next-line no-console
      console.debug && console.debug('[HelpChatIcon] session parsed', j && { hasText: !!j.text, submit: j.submit, ts: j.ts, scope: j.scope });
      if (!j || typeof j.text !== 'string') return null;
      if (typeof j.ts === 'number' && Date.now() - j.ts > 15000) return null; // TTL 15s
      return j;
    } catch (_) { return null; }
  }
  function trySeed(seed, attempts = 10, intervalMs = 150) {
    if (!seed) {
      const s = readSeedFromSession();
      if (!s) return;
      seed = s.text || '';
    }
    if (!seed) return;
    let tries = 0;
    const tick = () => {
      tries += 1;
      // eslint-disable-next-line no-console
      console.debug && console.debug('[HelpChatIcon] trySeed: attempt', tries, 'of', attempts);
      try {
        const panelHost = document.querySelector('[data-skinny-panel]');
        const sr = panelHost && panelHost.shadowRoot;
        if (sr) {
          // eslint-disable-next-line no-console
          console.debug && console.debug('[HelpChatIcon] trySeed: panel shadowRoot ready');
          const candidates = Array.from(sr.querySelectorAll('textarea, input[type="text"], [contenteditable="true"], [role="textbox"]'))
            .filter(el => {
              try {
                const rect = el.getBoundingClientRect();
                const enabled = !el.disabled && el.getAttribute('aria-disabled') !== 'true';
                const visible = !!el.offsetParent || (rect && rect.width > 0 && rect.height > 0);
                return enabled && visible;
              } catch (_) { return false; }
            });
          const input = candidates[0] || null;
          if (input) {
            // eslint-disable-next-line no-console
            console.debug && console.debug('[HelpChatIcon] trySeed: input found, seeding text');
            // Write text depending on element type
            try {
              input.focus();
              // Prefer InputEvent/execCommand to hit controlled editors
              let seeded = false;
              try {
                const ev1 = new InputEvent('beforeinput', { inputType: 'insertText', data: seed, bubbles: true, cancelable: true });
                input.dispatchEvent(ev1);
              } catch(_) {}
              try {
                const ev2 = new InputEvent('input', { inputType: 'insertText', data: seed, bubbles: true });
                input.dispatchEvent(ev2);
                seeded = true;
              } catch(_) {}
              if (!seeded) {
                try {
                  document.execCommand && document.execCommand('insertText', false, seed);
                  seeded = true;
                } catch(_) {}
              }
              if (!seeded) {
                // Last resort direct assignment + input event
                if (input.tagName === 'TEXTAREA' || (input.tagName === 'INPUT' && input.type === 'text')) {
                  input.value = seed;
                } else {
                  input.textContent = seed;
                }
                try { input.dispatchEvent(new Event('input', { bubbles: true })); } catch(_) {}
              }
            } catch (_) {}
            // Try explicit send button first
            const sendBtn = Array.from(sr.querySelectorAll('button,[role="button"]')).find(b => {
              const t = (b.textContent || '').trim().toLowerCase();
              const a = (b.getAttribute('aria-label') || '').trim().toLowerCase();
              return t === 'send' || a === 'send';
            }) || sr.querySelector('[data-send], .send, [data-testid="send"]');
            if (sendBtn) {
              // eslint-disable-next-line no-console
              console.debug && console.debug('[HelpChatIcon] trySeed: send button found, clicking');
              try { sendBtn.click(); } catch (_) {}
              return;
            }
            // Fallback: press Enter to send
            try {
              // eslint-disable-next-line no-console
              console.debug && console.debug('[HelpChatIcon] trySeed: send button not found, pressing Enter');
              const kd = new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true });
              const kp = new KeyboardEvent('keypress', { key: 'Enter', code: 'Enter', bubbles: true });
              const ku = new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', bubbles: true });
              input.dispatchEvent(kd); input.dispatchEvent(kp); input.dispatchEvent(ku);
              return;
            } catch (_) {}
          }
        } else {
          // eslint-disable-next-line no-console
          console.debug && console.debug('[HelpChatIcon] trySeed: panel not ready yet');
        }
      } catch (_) {}
      if (tries < attempts) setTimeout(tick, intervalMs);
      else {
        // eslint-disable-next-line no-console
        console.debug && console.debug('[HelpChatIcon] trySeed: giving up after', attempts, 'attempts');
      }
    };
    setTimeout(tick, intervalMs);
  }
  function handleClick(e) {
    try { if (e) { e.preventDefault(); e.stopPropagation(); } } catch(_) {}
    // eslint-disable-next-line no-console
    console.debug && console.debug('[HelpChatIcon] click: opening with seed', !!seedQuestion);
    // Store seed in sessionStorage for the widget (or robust fallback) to consume
    try {
      if (seedQuestion) {
        const payload = { text: seedQuestion, submit: true, ts: Date.now(), scope: scope || 'default' };
        // eslint-disable-next-line no-console
        console.debug && console.debug('[HelpChatIcon] session write SKINNY_SEED =', payload);
        sessionStorage.setItem('SKINNY_SEED', JSON.stringify(payload));
        // Re-read to confirm
        const verify = sessionStorage.getItem('SKINNY_SEED');
        // eslint-disable-next-line no-console
        console.debug && console.debug('[HelpChatIcon] session verify read SKINNY_SEED =', verify);
      }
    } catch(_) {}
    if (typeof onOpen === 'function') { try { onOpen(); } catch(_) {} }
    const opened = defaultOpen();
    // eslint-disable-next-line no-console
    console.debug && console.debug('[HelpChatIcon] click: launcher opened =', opened);
    if (seedQuestion) {
      // If we didn't find the launcher, still attempt to seed after a short delay
      trySeed(seedQuestion, 40, 150);
    }
  }
  function handleKeyDown(e) {
    if (e && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleClick();
    }
  }
  return (
    <OverlayTrigger placement={placement} delay={{ show: 150, hide: 0 }} overlay={<Tooltip id="help-chat-icon-tip" className="bg-dark">{title}</Tooltip>}>
      <span className={className} role="button" tabIndex={0} onMouseDown={(e)=>{ try{ e.preventDefault(); e.stopPropagation(); }catch(_){} }} onClick={handleClick} onKeyDown={handleKeyDown} aria-label="Open chat help">
        <svg xmlns="http://www.w3.org/2000/svg" width={dim} height={dim} fill="currentColor" role="img" aria-label="help" viewBox="0 0 16 16" style={mergedStyle}>
          <path d="M2.678 11.894a1 1 0 0 1 .287.801 11 11 0 0 1-.398 2c1.395-.323 2.247-.697 2.634-.893a1 1 0 0 1 .71-.074A8 8 0 0 0 8 14c3.996 0 7-2.807 7-6s-3.004-6-7-6-7 2.808-7 6c0 1.468.617 2.83 1.678 3.894m-.493 3.905a22 22 0 0 1-.713.129c-.2.032-.352-.176-.273-.362a10 10 0 0 0 .244-.637l.003-.01c.248-.72.45-1.548.524-2.319C.743 11.37 0 9.76 0 8c0-3.866 3.582-7 8-7s8 3.134 8 7-3.582 7-8 7a9 9 0 0 1-2.347-.306c-.52.263-1.639.742-3.468 1.105"/>
          <text x="8" y={questionYOffset} textAnchor="middle" fontSize="8" fill="currentColor" fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif">?</text>
        </svg>
      </span>
    </OverlayTrigger>
  );
}


